// ============================================================================
// REPORTING DOMAIN API CLIENT
// ============================================================================
// Domain-specific API for reports, templates, and report executions
// ============================================================================

import { ApiClient } from '../../core/apiClient';

// ============================================================================
// TYPES (matching backend ReportService types)
// ============================================================================

export type ReportType = 'compliance_summary' | 'compliance_detailed' | 'audit_trail' | 'risk_assessment' | 'performance_metrics' | 'user_activity' | 'framework_compliance' | 'custom';

export type ReportFormat = 'pdf' | 'excel' | 'csv' | 'json' | 'html';

export type ExecutionStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface ReportSchedule {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  dayOfWeek?: number;
  dayOfMonth?: number;
  time: string;
  timezone: string;
  nextRun?: Date;
}

export interface ReportFilter {
  field: string;
  operator: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'nin' | 'between' | 'contains';
  value: any;
}

export interface ReportPermission {
  userId: number;
  role: string;
  permissions: ('view' | 'edit' | 'delete' | 'share')[];
}

export interface ReportConfig {
  id: string;
  name: string;
  description: string;
  type: ReportType;
  template: string;
  format: ReportFormat;
  schedule?: ReportSchedule;
  recipients: string[];
  filters?: ReportFilter[];
  permissions: ReportPermission[];
  createdBy: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReportExecution {
  id: string;
  reportId: string;
  status: ExecutionStatus;
  format: ReportFormat;
  filePath?: string;
  fileSize?: number;
  errorMessage?: string;
  executionTime: number;
  executedBy: number;
  executedAt: Date;
  completedAt?: Date;
  parameters?: Record<string, any>;
}

export interface ReportTemplate {
  id: string;
  name: string;
  type: ReportType;
  content: string;
  styles?: string;
  variables: ReportTemplateVariable[];
  createdBy: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReportTemplateVariable {
  name: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'array' | 'object';
  description: string;
  required: boolean;
  defaultValue?: any;
}

export interface CreateReportRequest {
  name: string;
  description: string;
  type: ReportType;
  template: string;
  format: ReportFormat;
  schedule?: ReportSchedule;
  recipients: string[];
  filters?: ReportFilter[];
  permissions?: ReportPermission[];
}

export interface UpdateReportRequest {
  name?: string;
  description?: string;
  type?: ReportType;
  template?: string;
  format?: ReportFormat;
  schedule?: ReportSchedule;
  recipients?: string[];
  filters?: ReportFilter[];
  permissions?: ReportPermission[];
}

export interface CreateTemplateRequest {
  name: string;
  type: ReportType;
  content: string;
  styles?: string;
  variables: ReportTemplateVariable[];
}

export interface UpdateTemplateRequest {
  name?: string;
  type?: ReportType;
  content?: string;
  styles?: string;
  variables?: ReportTemplateVariable[];
}

export interface ExecuteReportRequest {
  reportId: string;
  userId?: number;
  parameters?: Record<string, any>;
}

export interface ReportQueryParams {
  type?: string;
  page?: number;
  limit?: number;
}

export interface ExecutionQueryParams {
  status?: string;
  page?: number;
  limit?: number;
}

// ============================================================================
// REPORTING API CLIENT CLASS
// ============================================================================

class ReportingApi extends ApiClient {
  constructor() {
    super({
      baseURL: '/api/report',
      enableLogging: import.meta.env.DEV,
    });
  }

  // ============================================================================
  // REPORT CRUD OPERATIONS
  // ============================================================================

  /**
   * Get all reports
   */
  async getReports(params?: ReportQueryParams): Promise<ReportConfig[]> {
    const response = await this.client.get('/', { params });
    return response.data.data || response.data || [];
  }

  /**
   * Get a single report by ID
   */
  async getReport(id: string): Promise<ReportConfig> {
    const response = await this.client.get(`/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create a new report
   */
  async createReport(data: CreateReportRequest): Promise<ReportConfig> {
    const response = await this.client.post('/', data);
    return response.data.data || response.data;
  }

  /**
   * Update an existing report
   */
  async updateReport(id: string, data: UpdateReportRequest): Promise<ReportConfig> {
    const response = await this.client.put(`/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete a report
   */
  async deleteReport(id: string): Promise<void> {
    await this.client.delete(`/${id}`);
  }

  // ============================================================================
  // REPORT EXECUTION OPERATIONS
  // ============================================================================

  /**
   * Execute a report
   */
  async executeReport(request: ExecuteReportRequest): Promise<ReportExecution> {
    const response = await this.client.post(`/${request.reportId}/execute`, request);
    return response.data.data || response.data;
  }

  /**
   * Get executions for a specific report
   */
  async getReportExecutions(reportId: string, params?: ExecutionQueryParams): Promise<ReportExecution[]> {
    const response = await this.client.get(`/${reportId}/executions`, { params });
    return response.data.data || response.data || [];
  }

  /**
   * Get a single execution by ID
   */
  async getExecution(id: string): Promise<ReportExecution> {
    const response = await this.client.get(`/executions/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Get all executions
   */
  async getAllExecutions(params?: ExecutionQueryParams): Promise<ReportExecution[]> {
    const response = await this.client.get('/executions', { params });
    return response.data.data || response.data || [];
  }

  /**
   * Download a report file
   */
  async downloadReport(executionId: string): Promise<Blob> {
    const response = await this.client.get(`/executions/${executionId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // ============================================================================
  // REPORT TEMPLATE OPERATIONS
  // ============================================================================

  /**
   * Create a new report template
   */
  async createTemplate(data: CreateTemplateRequest): Promise<ReportTemplate> {
    const response = await this.client.post('/templates', data);
    return response.data.data || response.data;
  }

  /**
   * Get all report templates
   */
  async getAllTemplates(params?: { type?: string }): Promise<ReportTemplate[]> {
    const response = await this.client.get('/templates', { params });
    return response.data.data || response.data || [];
  }

  /**
   * Get a single template by ID
   */
  async getTemplate(id: string): Promise<ReportTemplate> {
    const response = await this.client.get(`/templates/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Update a template
   */
  async updateTemplate(id: string, data: UpdateTemplateRequest): Promise<ReportTemplate> {
    const response = await this.client.put(`/templates/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete a template
   */
  async deleteTemplate(id: string): Promise<void> {
    await this.client.delete(`/templates/${id}`);
  }

  /**
   * Get dashboard stats
   */
  async getDashboardStats(): Promise<any> {
    const response = await this.client.get('/dashboard/stats');
    return response.data.data || response.data;
  }

  /**
   * Get compliance records
   */
  async getComplianceRecords(): Promise<any[]> {
    const response = await this.client.get('/compliance/records');
    return response.data.data || response.data || [];
  }

  /**
   * Get journey aligned report
   */
  async getJourneyAlignedReport(reportId: string): Promise<any> {
    const response = await this.client.get(`/journey/${reportId}`);
    return response.data.data || response.data;
  }

  /**
   * Generate journey aligned report
   */
  async generateJourneyAlignedReport(reportId: string, format: string): Promise<any> {
    const response = await this.client.post(`/journey/${reportId}/generate`, { format });
    return response.data.data || response.data;
  }
}

export const reportingApi = new ReportingApi();
export default reportingApi;
